import {LinkText} from '@/components/atoms';
import CustomDataTable from '@/components/organisms/CustomDataTable/CustomDataTable';
import {getCellDataMappedCols} from '@/helpers/dataTableCellDataMapper';
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils';
import {
  API_STATUS,
  DISABLE_HEADERS,
  STATUS_VALUES
} from '@/utils/constants/commonConstants';
import {useSalesTargets} from '@/hooks/useSalesTargets';
import {getSalesTargetsReporteDownload} from '@/services/salesTargets';
import {useState, useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {ROUTES} from '@/utils/constants/routesConstants';
import {
  DROP_SIZE_TYPES,
  EARLY_PAYMENT_TYPES
} from '@/utils/constants/saleTargetConstants';

const getReportLabel = (type) => {
  if (EARLY_PAYMENT_TYPES.includes(type)) return 'Early Payment Report';
  if (DROP_SIZE_TYPES.includes(type)) return 'Drop Size Report';
  return 'Sales Target Report';
};

// eslint-disable-next-line
export const cellDataMapperObj = navigate => ({
  salesTargetName: {
    cellData: ({row: {original} = {}}) => (
      <LinkText
        text={original?.salesTargetName || '—'}
        id={original?.salesTargetName + ' ' + (original?.id || original?.uuid)}
        handleClick={() =>
          navigate(
            `${ROUTES.SALES_TARGET_DETAIL.replace(':id', encodeURIComponent(original?.id || original?.uuid))}?status=${encodeURIComponent(original?.status)}`
          )
        }
      />
    ),
    linkHandler: (row) =>
      navigate(
        `${ROUTES.SALES_TARGET_DETAIL.replace(':id', encodeURIComponent(row?.id || row?.uuid))}?status=${encodeURIComponent(row?.status)}`
      ),
    linkLabel: (row) => row.salesTargetName || '—'
  },
  numEligibleStores: {
    cellData: ({row: {original} = {}}) => (
      <div>{original?.numEligibleStores ?? '—'}</div>
    )
  },
  percentTargetAchieved: {
    render: (row) => {
      if (
        EARLY_PAYMENT_TYPES.includes(row?.type) ||
        DROP_SIZE_TYPES.includes(row?.type)
      ) {
        return <div>{'\u2014'}</div>;
      }
      return <div>{row?.percentTargetAchieved ?? '\u2014'}</div>;
    }
  },
  pointsAwarded: {
    render: (row) => {
      if (
        EARLY_PAYMENT_TYPES.includes(row?.type) ||
        DROP_SIZE_TYPES.includes(row?.type)
      ) {
        return <div>{'\u2014'}</div>;
      }
      return <div>{row?.pointsAwarded ?? '\u2014'}</div>;
    }
  },
  createdAt: {
    cellData: ({row: {original} = {}}) => (
      <div>{original?.createdAt ?? original?.startDate ?? '—'}</div>
    )
  },
  endDate: {
    cellData: ({row: {original} = {}}) => <div>{original?.endDate ?? '—'}</div>
  },
  status: {
    cellData: ({row: {original} = {}}) => (
      <div
        className={
          original?.status === STATUS_VALUES.Error ? 'status_error' : ''
        }>
        {original?.status ?? '—'}
      </div>
    ),
    render: (row) => (
      <div className={row.status === STATUS_VALUES.Error ? 'status_error' : ''}>
        {row.status}
      </div>
    )
  },
  reportUrl: {
    cellData: ({row: {original} = {}}) => {
      const reportLabel = getReportLabel(original?.type);
      if (original.status?.toLowerCase() === API_STATUS.DRAFT?.toLowerCase()) {
        return <div className='text-gray-500'>{reportLabel}</div>;
      }
      return (
        <LinkText
          text={reportLabel}
          id={original?.id}
          handleClick={() =>
            onDownloadForSalesTargetsReport(
              original.id,
              original.status,
              original.type
            )
          }
        />
      );
    },
    downloadHandler: (row) =>
      onDownloadForSalesTargetsReport(row.id, row.status, row.type),
    downloadLabel: (row) => getReportLabel(row?.type),
    render: (row, column) => {
      if (row.status?.toLowerCase() === API_STATUS.DRAFT?.toLowerCase()) {
        return (
          <div className='px-2 py-1 text-gray-500'>
            {column.downloadLabel(row)}
          </div>
        );
      }
      return (
        <LinkText
          text={column.downloadLabel(row)}
          id={row.id}
          handleClick={() => column.downloadHandler(row)}
        />
      );
    }
  },
  lastUpdated: {
    cellData: ({row: {original} = {}}) => (
      <div>{original?.lastUpdated ?? original?.lastUpdatedDate ?? '—'}</div>
    )
  },
  updatedBy: {
    cellData: ({row: {original} = {}}) => (
      <div>{original?.updatedBy ?? '—'}</div>
    )
  }
});

const onDownloadForSalesTargetsReport = async (selectedIds, status, type) => {
  if (status?.toLowerCase() === API_STATUS.DRAFT?.toLowerCase()) {
    return;
  } else {
    const data = await getSalesTargetsReporteDownload(
      `salesTargetId=${selectedIds}`
    );
    const dateStr = getDateWithTimeString();
    let fileName = `SalesTarget_Report${dateStr}`;
    if (EARLY_PAYMENT_TYPES.includes(type)) {
      fileName = `EarlyPayment_Invoice_Report${dateStr}`;
    } else if (DROP_SIZE_TYPES.includes(type)) {
      fileName = `DropSizePoint_Invoice_Report${dateStr}`;
    }
    downloadCSVFiles(data, fileName);
  }
};

export const SalesTargetList = () => {
  const navigate = useNavigate();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [rowSelected, setRowSelected] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const {
    salesTargetsData,
    salesTargetsColConfigData,
    pageIndex,
    setPageIndex,
    setSortingObj,
    sortingObj,
    setSearchArry,
    onDownloadBtnClick,
    isLoading,
    handleResetAll
  } = useSalesTargets();

  const cellDataMappedCols = getCellDataMappedCols(
    cellDataMapperObj(navigate),
    salesTargetsColConfigData
  );

  const handleRowSelect = useCallback((selectedRecords, isAll) => {
    setRowSelected(selectedRecords);
    setIsSelectAll(isAll);
  }, []);

  const onDownloadClick = useCallback(
    async (selectedRows, selectionMeta = {}) => {
      const ids =
        selectedRows?.length > 0
          ? selectedRows.map((item) => item?.id || item?.uuid)
          : [];
      const excludeIds = Array.isArray(selectionMeta?.excludeIds)
        ? selectionMeta.excludeIds
        : [];
      onDownloadBtnClick(ids, excludeIds);
    },
    [onDownloadBtnClick]
  );

  return (
    <div className='salesTargetsDataTable'>
      <CustomDataTable
        columns={cellDataMappedCols}
        records={salesTargetsData?.data || []}
        isLoading={isLoading}
        pageSize={10}
        totalRecords={salesTargetsData?.totalRecords || 0}
        paginationConfig={{
          pageIndex,
          setPageIndex
        }}
        sortingConfig={{
          handleSorting: setSortingObj,
          initialSortConfig: sortingObj
        }}
        filterConfig={{
          setSearchArry,
          onResetAll: handleResetAll,
          disabledHeaders: DISABLE_HEADERS.SALES_TARGET_HEADERS
        }}
        selectionConfig={{
          onRowSelect: handleRowSelect,
          isSelectAll,
          globalSelectionEnable: true
        }}
        toolbarConfig={{
          onDownload: onDownloadClick,
          showDownload: true
        }}
      />
      {salesTargetsData?.totalRecords > 0 && (
        <div className='txtRightAlign position-absolute'>
          {salesTargetsData?.totalRecords} total items
        </div>
      )}
    </div>
  );
};
